<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-3xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Create New Invoice
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to create a new invoice.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="grid grid-cols-12 gap-4 pt-6 pb-5">
                                                <div class="col-span-12">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                                        Invoice Information
                                                    </h3>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="reference" class="block text-sm text-gray-900 required">
                                                        Reference
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="reference" v-model="form.reference"
                                                               disabled
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.reference}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="invoiced_at"
                                                           class="block text-sm font-medium text-gray-900 required">
                                                        Invoice Date
                                                    </label>
                                                    <div class="mt-1">
                                                        <input v-model="form.invoiced_at" id="invoiced_at" type="date"
                                                               readonly disabled
                                                               class="block w-full shadow-sm sm:text-sm disabled:opacity-50 focus:ring-amaze-coral focus:border-amaze-coral border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.invoiced_at}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="due_at" class="block text-sm text-gray-900 required">
                                                        Due Date
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="date" id="due_at" v-model="form.due_at"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.due_at}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="language" class="block text-sm text-gray-900 required">
                                                        Language
                                                    </label>
                                                    <div class="mt-1">
                                                        <select v-model="form.language" id="language"
                                                                :class="{'border-red-500': form.errors.language}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value="">Select a language</option>
                                                            <option v-for="(language, index) in languages"
                                                                    :value="index"
                                                                    v-html="language"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-12">
                                                    <label for="additional_company_name" class="block text-sm text-gray-900">
                                                        Additional Company Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="additional_company_name" v-model="form.additional_company_name"
                                                                class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                :class="{'border-red-500': form.errors.additional_company_name}"
                                                            />
                                                    </div>
                                                </div>

                                                <div class="col-span-12">
                                                    <label for="region_id" class="block text-sm text-gray-900 required">
                                                        Address
                                                    </label>
                                                    <div class="mt-1">
                                                        <textarea type="text" class="block w-full shadow-sm sm:text-sm focus:ring-amaze-coral focus:border-amaze-coral border-gray-300 rounded-md
                                                           " v-model="form.address"/>
                                                    </div>
                                                </div>

                                                <div class="col-span-6">
                                                    <label for="event_at" class="block text-sm text-gray-900 required">
                                                        Event Date
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="date" id="event_at" v-model="form.event_at"
                                                               class="block w-full shadow-sm sm:text-sm disabled:opacity-50 focus:ring-amaze-coral focus:border-amaze-coral border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.event_at}"
                                                               readonly disabled
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-12">
                                                    <label for="po_reference"
                                                           class="block text-sm text-gray-900">
                                                        Purchase Order Reference
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="po_reference" v-model="form.po_reference"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.po_reference}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-12">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                                        Deposit Information
                                                    </h3>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="is_final"
                                                           class="block text-sm font-medium text-gray-900">
                                                        Is Final Invoice
                                                    </label>
                                                    <div class="mt-1">
                                                        <button type="button" @click="form.is_final = !form.is_final"
                                                                :class="{'bg-amaze-coral': form.is_final, 'bg-gray-200': !form.is_final }"
                                                                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none"
                                                                role="switch" aria-checked="false">
                                                            <span class="sr-only">Is Final Invoice</span>
                                                            <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                                                            <span aria-hidden="true"
                                                                  :class="[form.is_final ? 'translate-x-5': 'translate-x-0' ]"
                                                                  class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="col-span-6">
                                                    <label for="is_final"
                                                           class="block text-sm font-medium text-gray-900">
                                                        Is Credit Note
                                                    </label>
                                                    <div class="mt-1">
                                                        <button type="button"
                                                                @click="form.is_credit_note = !form.is_credit_note"
                                                                :class="{'bg-amaze-coral': form.is_credit_note, 'bg-gray-200': !form.is_credit_note }"
                                                                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none"
                                                                role="switch" aria-checked="false">
                                                            <span class="sr-only">Is Credit Note</span>
                                                            <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                                                            <span aria-hidden="true"
                                                                  :class="[form.is_credit_note ? 'translate-x-5': 'translate-x-0' ]"
                                                                  class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="col-span-6"></div>
                                                <div class="col-span-12 grid grid-cols-12 gap-4" v-if="!form.is_final">
                                                    <div class="col-span-6">
                                                        <label for="amount"
                                                               class="block text-sm text-gray-900 required">
                                                            Amount
                                                        </label>
                                                        <div class="mt-1">
                                                            <input type="text" id="amount" v-model="form.amount"
                                                                   class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                                   :class="{'border-red-500': form.errors.amount}"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="col-span-6 pt-5">
                                                        <p class="block text-sm font-medium text-gray-900">Gross Total:
                                                            {{ formatCurrency(form.gross_total, project.bank_account.currency.code, true) }}</p>
                                                        <p class="block text-sm font-medium text-gray-900">Total Amount
                                                            Paid: {{
                                                               formatCurrency(form.total_amount_paid, project.bank_account.currency.code, true)
                                                            }}</p>
                                                    </div>
                                                    <div class="col-span-6">
                                                        <label for="is_final"
                                                               class="block text-sm font-medium text-gray-900">
                                                            Is Percentage
                                                        </label>
                                                        <div class="mt-1">
                                                            <button type="button"
                                                                    @click="form.is_percentage = !form.is_percentage"
                                                                    :class="{'bg-amaze-coral': form.is_percentage, 'bg-gray-200': !form.is_percentage }"
                                                                    class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none"
                                                                    role="switch" aria-checked="false">
                                                                <span class="sr-only">Is Percentage</span>
                                                                <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                                                                <span aria-hidden="true"
                                                                      :class="[form.is_percentage ? 'translate-x-5': 'translate-x-0' ]"
                                                                      class="translate-x-0 pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                                            </button>
                                                        </div>
                                                        <p class="text-xs text-gray-800 mt-1">Enter a percentage without the % symbol</p>
                                                    </div>
                                                </div>
                                                <div class="col-span-12">
                                                    <p class="text-xs text-gray-800">If you require a breakdown of VAT to be displayed on your deposits. The <a class="underline" href="'/admin/offices/">office</a> assigned must have this enabled.</p>
                                                </div>
                                                <div class="col-span-12">
                                                    <label for="payment_instructions"
                                                           class="block text-sm text-gray-900">
                                                        Payment Instructions
                                                    </label>
                                                    <div class="mt-1">
                                                        <quill-editor content-type="html"
                                                                      v-model:content="form.payment_instructions"
                                                                      :class="{'border-red-500': form.errors.payment_instructions}"
                                                                      class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                      theme="snow"></quill-editor>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="button"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-200"
                                            @click="previewInvoice()">
                                        <span>Preview</span>
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {
    LinkIcon,
    PlusSmIcon,
    QuestionMarkCircleIcon,
    ExclamationCircleIcon,
    ArrowCircleRightIcon,
    RefreshIcon
} from '@heroicons/vue/solid'
import {useForm, usePage, router} from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import moment from 'moment';
import axios from 'axios';


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        ArrowCircleRightIcon,
        RefreshIcon
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            project: computed(() => usePage().props.project),
            quote: computed(() => usePage().props.quote),
            languages: ref(),
            form: useForm({
                reference: null,
                po_reference: null,
                invoiced_at: null,
                due_at: null,
                amount: null,
                final_total: null,
                payment_instructions: null,
                is_final: false,
                is_credit_note: false,
                office_id: null,
                bank_account_id: null,
                currency_id: null,
                status: null,
                language: null,
                address: null,
                additional_company_name: null,
                is_percentage: false,
                event_at: null
            })
        }
    },
    methods: {
        submit() {

            this.form.amount = this.form.amount.replace(',','.');
            this.form.post(route('frontend.project.quote.invoices.create', [this.project.id, this.quote.id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                    this.form.amount = this.formatCurrency(this.form.amount, this.project.bank_account.currency.code)
                }
            });
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.form.processing = false;
            this.show = false;
        },
        open() {
            this.form.clearErrors();
            this.show = true;

            // get the invoice config
            this.getConfig();
        },
        /**
         * Get and set the new invoice config
         */
        getConfig() {
            axios.get(route('frontend.project.quote.invoices.new-invoice-config', [this.project.id, this.quote.id])).then(result => {
                // loop over each key returned from the config json
                for (var key in result.data.form) {
                    // set it against our new invoice form model
                    this.form[key] = result.data.form[key];
                    if(key === 'amount') {
                        this.form[key] = this.formatCurrency(result.data.form[key], this.project.bank_account.currency.code);
                    } else {
                        this.form[key] = result.data.form[key];
                    }
                }
                this.countries = result.data.countries;
                this.languages = result.data.languages;
            });
        },
        /**
         * Previews the invoice
         */
        previewInvoice() {
            this.form.processing = true;
            router.on('start', () => NProgress.start());
            axios({
                url: route('frontend.project.quote.invoices.preview', [this.project.id, this.quote.id]),
                method: "POST",
                data: this.form,
                responseType: 'arraybuffer'
            }).then(r => {
                const path = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement('a');
                link.href = path;
                link.setAttribute('download', 'DRAFT-' + this.form.reference + '.pdf');
                document.body.appendChild(link);
                link.click(); // force download file
                this.form.processing = false;
                router.on('finish', () => NProgress.done());
            });
        }
    },
    computed: {
        amountData() {
            const amount = this.form.amount;
            const is_percentage = this.form.is_percentage;

            return {
                amount,
                is_percentage
            }
        }
    },
    watch: {
        "form.is_percentage": {
            deep: true,
            handler(val) {
                let amount = String(this.form.amount ?? 0).replace(',', '.').replace(/\.(?=.*\.)/, '').replace(/[^0-9\.\-]/, '')
                // If the amount is not a number, there's nothing we can do!
                if(isNaN(amount)) {
                    return false;
                }

                let maximum_amount = this.form.gross_total - this.form.total_amount_paid;

                // If the percentage entered is more than 100. Reset it to the maximum allowed amount of 100.
                this.form.amount = (val === true && amount > 100) ? 100 : this.formatCurrency(maximum_amount, this.project.bank_account.currency.code);

            }
        },
        "form.amount": {
            deep: true,
            handler(val) {

                // If the value entered is not a number, do nothing.
                if(isNaN(val)) {
                    return false;
                }

                // If the percentage entered is more than 100. Reset it to the maximum allowed amount of 100.
                if(this.form.is_percentage && this.form.amount > 100) {
                    this.form.amount = 100;
                }

                // Get the maximum allowed value to be entered.
                let maximum_amount = this.form.gross_total - this.form.total_amount_paid;

                // If the amount entered is more than the maximum allowed value, reset the value to the maximum value.
                if(val > maximum_amount) {
                    this.form.amount = maximum_amount;
                }
            }
        }
    }
}
</script>
